import React from 'react';
import { useTranslation } from 'next-i18next';
import classnames from 'classnames';

import { CookieBanner, ButtonVariants as Variants } from 'components/ui';

import { CONSENT_COOKIE_TYPES } from 'types';
import type { IConsent, IConsentCookie } from 'types';

import styles from './ConsentBanner.module.scss';

const ACTION_TYPES = {
  CONFIGURE: 'CONFIGURE',
  DECLINE: 'DECLINE',
  ACCEPT: 'ACCEPT',
};

const ACTIONS = [
  // Accept
  {
    id: ACTION_TYPES.ACCEPT,
    className: styles.actionBtn,
    text: 'COOKIES.BANNER.ACCEPT',
    variant: Variants.PRIMARY,
    isPrimary: true,
    dataTestId: 'cookies-banner-accept'
  },
  // Decline
  {
    id: ACTION_TYPES.DECLINE,
    className: styles.actionBtn,
    text: 'COOKIES.BANNER.DECLINE',
    variant: Variants.EMPTY,
    dataTestId: 'cookies-banner-decline'
  },
  // Configure
  {
    id: ACTION_TYPES.CONFIGURE,
    className: classnames(styles.actionBtn, styles.configureBtn),
    text: 'COOKIES.BANNER.CONFIGURE',
    variant: Variants.EMPTY,
    dataTestId: 'cookies-banner-configure'
  },
];

interface Props {
  cookies: IConsentCookie[],
  isOpen: boolean,
  //
  onUpdateConsent: (updatedConsent: IConsent, onSuccess: () => void, onError?: () => void) => void,
  onConfigureClick: () => void,
  //
  onClose?: () => void
}

const ConsentBanner = (props: Props) => {

  const {
    cookies,
    isOpen,
    //
    onUpdateConsent,
    onConfigureClick,
    //
    onClose
  } = props;

  // Hooks

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = React.useState(false);

  // Handlers

  const handleConfigureClick = () => {
    onConfigureClick();
    if (onClose) {
      onClose();
    }
  };

  const handleUpdateConsent = (isAccepted: boolean) => {
    const updatedCookies = cookies
      .filter((cookie) => !cookie?.hasSetConsent)
      .map((cookie) => ({
        ...cookie,
        isAccepted: cookie.type === CONSENT_COOKIE_TYPES.FUNCTIONAL || isAccepted
      }));

    setIsLoading(true);
    onUpdateConsent(
      {
        timestamp: undefined,
        cookies: updatedCookies,
        shouldRequestConsent: false,
        hasGivenConsent: true
      },
      () => {
        setIsLoading(false);
        if (onClose) {
          onClose();
        }
      },
      () => {
        setIsLoading(false);
      }
    );
  };

  const handleAction = (actionId: string) => {
    switch (actionId) {
      case ACTION_TYPES.CONFIGURE:
        handleConfigureClick();
        break;

      case ACTION_TYPES.DECLINE:
        handleUpdateConsent(false);
        break;

      case ACTION_TYPES.ACCEPT:
        handleUpdateConsent(true);
        break;

      default:
        console.warn(`CookieBanner - Action ${actionId} not supported`);
    }
  };

  // Render

  return (
    <CookieBanner
      isOpen={isOpen}
      buttonClassName={styles.btn}
      buttonsContainerClassName={styles.btnContainer}
      //
      title={t('COOKIES.BANNER.TITLE')}
      description={t('COOKIES.BANNER.DESCRIPTION')}
      actions={ACTIONS}
      //
      isLoading={isLoading}
      //
      onAction={handleAction}
      onClose={onClose}
      //
      dataTestId="cookies-banner"
    />
  );
};

export default ConsentBanner;
